import { useExamCenter } from '@exam-center/admin-exam-center-details';
import { useConfig } from '@exam-center/configuration';
import {
  ExamCenterMemberType,
  AdminGetExamCenterMembersByExamCenterCodeDocument,
  GetExamCenterMembersByExamCenterCodeDocument,
  OrderType,
  useAddMembersToExamCenterMutation,
  useSearchAvailableExamCenterManagerUsersQuery,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormButton,
  GroupOutlinedIcon,
  Loading,
  NoRowsOverlay,
  OpenInNewOutlinedIcon,
  styled,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AddExamCenterMemberList } from '../add-exam-center-member-list';

const StyledDialogContent = styled(DialogContent)({
  height: '600px',
});

const AddExamCenterMemberSchema = z.object({
  members: z.array(z.string()),
});
type AddExamCenterMemberFormInput = z.infer<typeof AddExamCenterMemberSchema>;

interface AddExamCenterMemberModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AddExamCenterMemberModal(props: Readonly<AddExamCenterMemberModalProps>) {
  const { open: isOpen, onClose } = props;

  const { examCenter } = useExamCenter();
  const { code: examCenterCode } = examCenter;

  const { showSnackbar } = useSnackbar();

  const [addMembersToExamCenter] = useAddMembersToExamCenterMutation();

  const { userManagement } = useConfig();

  const { data: availableExamCenterManagerUsersData, loading: isLoadingAvailableUsersData } =
    useSearchAvailableExamCenterManagerUsersQuery({
      variables: {
        searchAvailableExamCenterManagerUsersInput: {
          paging: {
            currentPage: 0,
            pageSize: 10,
          },
          order: {
            field: 'firstname',
            type: OrderType.Asc,
          },
        },
      },
      fetchPolicy: 'network-only',
      skip: !isOpen,
    });

  const formContext = useForm<AddExamCenterMemberFormInput>({
    defaultValues: {
      members: [],
    },
    resolver: zodResolver(AddExamCenterMemberSchema),
  });

  const handleAddExamCenterMember = async (data: AddExamCenterMemberFormInput) => {
    const { members } = data;
    await addMembersToExamCenter({
      variables: {
        addMembersToExamCenterInput: {
          examCenterCode: examCenterCode,
          members: members.map((member) => ({
            type: ExamCenterMemberType.Manager,
            userId: member,
          })),
        },
      },
      refetchQueries: [
        {
          query: AdminGetExamCenterMembersByExamCenterCodeDocument,
          variables: {
            getExamCenterMembersByExamCenterCodeInput: {
              examCenterCode: examCenterCode,
            },
          },
        },
        GetExamCenterMembersByExamCenterCodeDocument,
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackbar({
          message: 'เพิ่มสมาชิกเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
        formContext.reset();
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถเพิ่มสมาชิกได้',
          variant: 'error',
        });
      },
    });
  };

  const handleSelectMembers = (selectedMembers: string[]) => {
    formContext.setValue('members', selectedMembers, { shouldDirty: true });
  };

  const handleGoToUserManagement = () => {
    window.open(userManagement.url, '_blank');
  };

  const isOptionsEmpty =
    availableExamCenterManagerUsersData?.ecSearchAvailableExamCenterManagerUsers.total === 0;

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='lg'
        slotProps={{
          paper: { component: Form, onSubmit: formContext.handleSubmit(handleAddExamCenterMember) },
        }}
      >
        <DialogTitle onClose={onClose}>เพิ่มสมาชิก</DialogTitle>
        <Divider />
        {isLoadingAvailableUsersData ? (
          <Loading sx={{ minHeight: '444px' }} />
        ) : isOptionsEmpty ? (
          <StyledDialogContent>
            <NoRowsOverlay
              icon={GroupOutlinedIcon}
              title='ยังไม่มีรายชื่อผู้จัดการศูนย์ทดสอบ'
              description={`กรุณาเพิ่มรายชื่อในระบบ User Management\nโดยกำหนด Role เป็น “ผู้จัดการศูนย์ทดสอบ” ก่อนจึงจะสามารถเลือกสมาชิกได้ที่นี่`}
              action={
                <Button
                  variant='outlined'
                  endIcon={<OpenInNewOutlinedIcon />}
                  onClick={handleGoToUserManagement}
                >
                  ไปที่ User Management
                </Button>
              }
            />
          </StyledDialogContent>
        ) : (
          <>
            <StyledDialogContent>
              <AddExamCenterMemberList onSelectRow={handleSelectMembers} />
            </StyledDialogContent>
            <Divider />
            <DialogActions>
              <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
                ยกเลิก
              </Button>
              <FormButton label='เพิ่มสมาชิก' size='large' />
            </DialogActions>
          </>
        )}
      </Dialog>
    </FormProvider>
  );
}
